/**
 * Gate 2 — Evidence step: Quality Scorecard (Figma G2-E3).
 * All figures and rows come from scored metric results + Gate 1 targets/risks.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Link,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EmptyState from "../../components/EmptyState";
import TruncatedCellLine from "../../components/TruncatedCellLine";
import { PORTFOLIO_TOOLTIP_SLOT_PROPS } from "../../components/portfolio/portfolioShared";
import { ReactComponent as RedBulletPoint } from "../../asserts/logo/RedBulletPoint.svg";
import { ReactComponent as BrownBulletPoint } from "../../asserts/logo/BrownBulletPoint.svg";
import { ReactComponent as NoCustomTestCasesIcon } from "../../asserts/logo/NoCustomTestCases.svg";
import { GHOST_PRIMARY_MEDIUM_BTN_SX, OUTLINED_BTN_SX } from "./gate2Tokens";
import {
  formatActualForScorecard,
  formatExpectedForScorecard,
  rowStatus,
  scorecardUnevaluatedDetail,
} from "./gate2MetricFormat";
import {
  buildScorecardBlockingLines,
  buildScorecardSummary,
  partitionRisksForScorecard,
} from "./gate2ScorecardSummary";
import ExecutionScopeItemDetailView from "./ExecutionScopeItemDetailView";
import { resolveScorecardMetricDetailItem } from "./gate2ScorecardMetricDetail";
import SelectDropdownTheme from "../../components/SelectDropdownTheme";
import AddTestCaseDialog from "../gate1/AddTestCaseDialog";
import {
  SCOPE_TABLE_WRAP_SX,
  SCOPE_TABLE_SX,
  SCOPE_TABLE_METRIC_NAME_CELL_SX,
  SCOPE_TABLE_METRIC_NAME_HEAD_SX,
  SCOPE_TABLE_METRIC_NAME_VALUE_SX,
  SCOPE_TABLE_TRUNCATE_NAME_CELL_SX,
  SCOPE_TABLE_THRESHOLD_HEAD_SX,
  SCOPE_TABLE_THRESHOLD_VALUE_SX,
  SCOPE_TABLE_TEST_CASES_HEAD_SX,
  SCOPE_TABLE_ACTIONS_HEAD_SX,
  SCOPE_VIEW_DETAILS_LINK_SX,
  TABLE_HEAD_CELL_SX,
  TABLE_BODY_CELL_SX,
} from "./gate2ScopeTableStyles";

const RISK_SUMMARY_TITLE_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 16,
  fontWeight: 900,
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#383838",
};

const RISK_SUMMARY_BLOCKING_HEADING_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 14,
  fontWeight: 900,
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#B82500",
};

const RISK_SUMMARY_MITIGATED_HEADING_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 14,
  fontWeight: 900,
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#8A5C00",
};

const RISK_SUMMARY_ITEM_TEXT_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 13,
  fontWeight: 400,
  lineHeight: "140%",
  letterSpacing: 0,
  color: "#555555",
};

const RISK_SUMMARY_EMPTY_SX = {
  ...RISK_SUMMARY_ITEM_TEXT_SX,
  color: "#94A3B8",
};

function RiskSummaryBulletItem({ text, BulletIcon }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          flexShrink: 0,
          width: 6,
          height: 6,
          mt: "6px",
        }}
        aria-hidden
      >
        <BulletIcon width={6} height={6} />
      </Box>
      <Typography component="span" sx={RISK_SUMMARY_ITEM_TEXT_SX}>
        {text}
      </Typography>
    </Box>
  );
}

function RiskSummaryBulletList({ items, BulletIcon }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      {items.map((item) => (
        <RiskSummaryBulletItem key={item.key} text={item.text} BulletIcon={BulletIcon} />
      ))}
    </Box>
  );
}

function StatusPill({ statusKey, label }) {
  const tone =
    statusKey === "passed"
      ? { bg: "#E8FCF3", color: "#00754E", border: "#86EFAC" }
      : statusKey === "failed"
        ? { bg: "#FEF2F2", color: "#B91C1C", border: "#FCA5A5" }
        : { bg: "#FFFBEB", color: "#B45309", border: "#FDE68A" };
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 72,
        height: 26,
        px: 1.25,
        borderRadius: "13px",
        fontFamily: "Lato, sans-serif",
        fontSize: 12,
        fontWeight: 700,
        bgcolor: tone.bg,
        color: tone.color,
        border: `1px solid ${tone.border}`,
      }}
    >
      {label}
    </Box>
  );
}

/** Figma — single white surface card wrapping the entire scorecard (right panel). */
const SCORECARD_SURFACE_CARD_SX = {
  border: "1px solid #E6E6E6",
  borderRadius: "12px",
  bgcolor: "#FFFFFF",
  p: { xs: 2, md: 3 },
  boxSizing: "border-box",
};

const SCORECARD_PANEL_LABEL_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 12,
  fontWeight: 700,
  fontStyle: "normal",
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#707070",
};

const SCORECARD_PANEL_VALUE_GREY_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 18,
  fontWeight: 700,
  fontStyle: "normal",
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#707070",
};

const SCORECARD_INNER_PANEL_SX = {
  flex: 1,
  minWidth: 0,
  border: "1px solid #E6E6E6",
  borderRadius: "8px",
  bgcolor: "#FFFFFF",
  px: 2.5,
  py: 2,
  display: "flex",
  flexDirection: "column",
  gap: 1.25,
  minHeight: 88,
};

function ScorecardInnerPanel({ title, children }) {
  return (
    <Box sx={SCORECARD_INNER_PANEL_SX}>
      <Typography sx={SCORECARD_PANEL_LABEL_SX}>{title}</Typography>
      {children}
    </Box>
  );
}

/** Scorecard table column widths — name column gets extra room; remaining cols share the rest. */
const SCORECARD_CUSTOM_TABLE_COL_WIDTHS = {
  name: "32%",
  linkedMetric: "16%",
  actual: "13%",
  expected: "13%",
  status: "12%",
  actions: "14%",
};

const SCORECARD_METRICS_TABLE_COL_WIDTHS = {
  name: "38%",
  actual: "18%",
  expected: "18%",
  status: "14%",
  actions: "12%",
};

const CUSTOM_TEST_CASES_EMPTY_ICON = (
  <NoCustomTestCasesIcon style={{ width: 64, height: 64, display: "block" }} aria-hidden />
);

const CUSTOM_TEST_CASES_EMPTY_COPY = {
  title: "No custom test cases added yet",
  description:
    "Custom test cases let you validate model behavior beyond standard metrics. Run your standard evaluation first, then create targeted tests for any gaps.",
};

const CUSTOM_TEST_CASES_EPHEMERAL_EMPTY_COPY = {
  title: "No saved custom test cases",
  description:
    "Run Once results are not listed here. Use Save & Execute to keep test case details in this table.",
};

function CustomTestCasesTable({ rows, onViewDetails }) {
  return (
    <TableContainer sx={SCOPE_TABLE_WRAP_SX}>
      <Table size="small" sx={SCOPE_TABLE_SX} aria-label="Custom test case results">
        <colgroup>
          <col style={{ width: SCORECARD_CUSTOM_TABLE_COL_WIDTHS.name }} />
          <col style={{ width: SCORECARD_CUSTOM_TABLE_COL_WIDTHS.linkedMetric }} />
          <col style={{ width: SCORECARD_CUSTOM_TABLE_COL_WIDTHS.actual }} />
          <col style={{ width: SCORECARD_CUSTOM_TABLE_COL_WIDTHS.expected }} />
          <col style={{ width: SCORECARD_CUSTOM_TABLE_COL_WIDTHS.status }} />
          <col style={{ width: SCORECARD_CUSTOM_TABLE_COL_WIDTHS.actions }} />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                ...TABLE_HEAD_CELL_SX,
                ...SCOPE_TABLE_METRIC_NAME_CELL_SX,
                ...SCOPE_TABLE_METRIC_NAME_HEAD_SX,
              }}
            >
              Test Case Name
            </TableCell>
            <TableCell sx={{ ...TABLE_HEAD_CELL_SX, ...SCOPE_TABLE_THRESHOLD_HEAD_SX }}>Linked Metric</TableCell>
            <TableCell sx={{ ...TABLE_HEAD_CELL_SX, ...SCOPE_TABLE_THRESHOLD_HEAD_SX }}>Actual Result</TableCell>
            <TableCell sx={{ ...TABLE_HEAD_CELL_SX, ...SCOPE_TABLE_THRESHOLD_HEAD_SX }}>Expected</TableCell>
            <TableCell sx={{ ...TABLE_HEAD_CELL_SX, ...SCOPE_TABLE_TEST_CASES_HEAD_SX }}>Status</TableCell>
            <TableCell sx={{ ...TABLE_HEAD_CELL_SX, ...SCOPE_TABLE_ACTIONS_HEAD_SX }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const status = rowStatus(row);
            const failed = status.key === "failed";
            return (
              <TableRow key={row.key} hover={false} selected={false}>
                <TableCell
                  sx={{
                    ...TABLE_BODY_CELL_SX,
                    ...SCOPE_TABLE_METRIC_NAME_CELL_SX,
                    ...SCOPE_TABLE_METRIC_NAME_VALUE_SX,
                    ...SCOPE_TABLE_TRUNCATE_NAME_CELL_SX,
                  }}
                >
                  <TruncatedCellLine
                    text={row.testCaseName}
                    sx={{ ...SCOPE_TABLE_METRIC_NAME_VALUE_SX, color: "#0F172A" }}
                  />
                </TableCell>
                <TableCell sx={{ ...TABLE_BODY_CELL_SX, ...SCOPE_TABLE_THRESHOLD_VALUE_SX, color: "#383838" }}>
                  {row.linkedMetricName}
                </TableCell>
                <TableCell
                  sx={{
                    ...TABLE_BODY_CELL_SX,
                    ...SCOPE_TABLE_THRESHOLD_VALUE_SX,
                    color: failed ? "#B91C1C" : "#383838",
                    fontWeight: failed ? 700 : 400,
                  }}
                >
                  {formatActualForScorecard(row)}
                </TableCell>
                <TableCell sx={{ ...TABLE_BODY_CELL_SX, ...SCOPE_TABLE_THRESHOLD_VALUE_SX, color: "#383838" }}>
                  {formatExpectedForScorecard(row.threshold, row.direction, row.unit)}
                </TableCell>
                <TableCell sx={{ ...TABLE_BODY_CELL_SX, ...SCOPE_TABLE_THRESHOLD_VALUE_SX }}>
                  <StatusPill statusKey={status.key} label={status.label} />
                </TableCell>
                <TableCell sx={{ ...TABLE_BODY_CELL_SX, whiteSpace: "nowrap" }}>
                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => onViewDetails(row.key)}
                    sx={SCOPE_VIEW_DETAILS_LINK_SX}
                  >
                    View Details
                  </Link>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function MetricsTable({ rows, onViewDetails }) {
  return (
    <TableContainer sx={SCOPE_TABLE_WRAP_SX}>
      <Table size="small" sx={SCOPE_TABLE_SX} aria-label="Standard metric results">
        <colgroup>
          <col style={{ width: SCORECARD_METRICS_TABLE_COL_WIDTHS.name }} />
          <col style={{ width: SCORECARD_METRICS_TABLE_COL_WIDTHS.actual }} />
          <col style={{ width: SCORECARD_METRICS_TABLE_COL_WIDTHS.expected }} />
          <col style={{ width: SCORECARD_METRICS_TABLE_COL_WIDTHS.status }} />
          <col style={{ width: SCORECARD_METRICS_TABLE_COL_WIDTHS.actions }} />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                ...TABLE_HEAD_CELL_SX,
                ...SCOPE_TABLE_METRIC_NAME_CELL_SX,
                ...SCOPE_TABLE_METRIC_NAME_HEAD_SX,
              }}
            >
              Test Case Name
            </TableCell>
            <TableCell sx={{ ...TABLE_HEAD_CELL_SX, ...SCOPE_TABLE_THRESHOLD_HEAD_SX }}>
              Actual Result
            </TableCell>
            <TableCell sx={{ ...TABLE_HEAD_CELL_SX, ...SCOPE_TABLE_THRESHOLD_HEAD_SX }}>Expected</TableCell>
            <TableCell sx={{ ...TABLE_HEAD_CELL_SX, ...SCOPE_TABLE_TEST_CASES_HEAD_SX }}>Status</TableCell>
            <TableCell sx={{ ...TABLE_HEAD_CELL_SX, ...SCOPE_TABLE_ACTIONS_HEAD_SX }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((result) => {
            const status = rowStatus(result);
            const unevaluatedDetail = scorecardUnevaluatedDetail(result);
            return (
              <TableRow key={result.key} hover={false} selected={false}>
                <TableCell
                  sx={{
                    ...TABLE_BODY_CELL_SX,
                    ...SCOPE_TABLE_METRIC_NAME_CELL_SX,
                    ...SCOPE_TABLE_METRIC_NAME_VALUE_SX,
                    ...SCOPE_TABLE_TRUNCATE_NAME_CELL_SX,
                  }}
                >
                  <TruncatedCellLine
                    text={result.metricName}
                    sx={{ ...SCOPE_TABLE_METRIC_NAME_VALUE_SX, color: "#0F172A" }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    ...TABLE_BODY_CELL_SX,
                    ...SCOPE_TABLE_THRESHOLD_VALUE_SX,
                    color: status.key === "failed" ? "#B91C1C" : "#383838",
                    fontWeight: status.key === "failed" ? 700 : 400,
                  }}
                >
                  {formatActualForScorecard(result)}
                </TableCell>
                <TableCell sx={{ ...TABLE_BODY_CELL_SX, ...SCOPE_TABLE_THRESHOLD_VALUE_SX, color: "#383838" }}>
                  {formatExpectedForScorecard(result.threshold, result.direction, result.unit)}
                </TableCell>
                <TableCell sx={{ ...TABLE_BODY_CELL_SX, ...SCOPE_TABLE_THRESHOLD_VALUE_SX }}>
                  <Tooltip
                    title={unevaluatedDetail || ""}
                    arrow
                    placement="top"
                    disableHoverListener={!unevaluatedDetail}
                    componentsProps={PORTFOLIO_TOOLTIP_SLOT_PROPS}
                  >
                    <span>
                      <StatusPill statusKey={status.key} label={status.label} />
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ ...TABLE_BODY_CELL_SX, whiteSpace: "nowrap" }}>
                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => onViewDetails(result.key)}
                    sx={SCOPE_VIEW_DETAILS_LINK_SX}
                  >
                    View Details
                  </Link>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function QualityScorecardStep({
  results,
  casesById,
  testCasesById,
  executionCases = [],
  risks = [],
  metrics = [],
  targetTrustIndex,
  customTestCaseCount = 0,
  customTestCaseRows = [],
  ephemeralCustomRunCount = 0,
  onAddCustomTestCase,
  metricItems = [],
  onMetricDetailChange,
  projectId = null,
}) {
  const [tab, setTab] = useState("standard");
  const [alertOpen, setAlertOpen] = useState(true);
  const [detailKey, setDetailKey] = useState("");
  const [caseDetailId, setCaseDetailId] = useState("");

  const summary = useMemo(
    () => buildScorecardSummary(results, targetTrustIndex),
    [results, targetTrustIndex]
  );
  const { blocking, mitigated } = useMemo(() => partitionRisksForScorecard(risks), [risks]);
  const blockingLines = useMemo(
    () => buildScorecardBlockingLines(results, executionCases),
    [results, executionCases]
  );

  const blockingBulletItems = useMemo(() => {
    if (blockingLines.length) {
      return blockingLines.map((text, idx) => ({ key: `line-${idx}`, text }));
    }
    return blocking.map((r) => ({ key: r.id, text: r.name }));
  }, [blockingLines, blocking]);

  const mitigatedBulletItems = useMemo(
    () =>
      mitigated.map((r) => ({
        key: r.id,
        text: [r.name, r.mitigation].filter(Boolean).join(" — "),
      })),
    [mitigated]
  );

  const customBelowThreshold = useMemo(
    () => customTestCaseRows.filter((r) => r.passed === false).length,
    [customTestCaseRows]
  );

  const qualityGateFailed = summary.failedCount > 0 || customBelowThreshold > 0;
  const qualityGateBannerFailedCount = summary.failedCount + customBelowThreshold;

  const detailResult =
    results.find((r) => r.key === detailKey) || customTestCaseRows.find((r) => r.key === detailKey);

  const flyoutCasesById = testCasesById || casesById;

  const executionCasesById = useMemo(() => {
    const map = new Map();
    if (casesById instanceof Map) {
      casesById.forEach((value, key) => {
        if (value?.id != null) map.set(String(value.id), value);
        else map.set(String(key), value);
      });
    }
    for (const c of executionCases || []) {
      if (c?.id != null) map.set(String(c.id), c);
    }
    return map;
  }, [casesById, executionCases]);

  const metricDetailItem = useMemo(() => {
    if (!detailResult) return null;
    return resolveScorecardMetricDetailItem(detailResult, metricItems, flyoutCasesById);
  }, [detailResult, metricItems, flyoutCasesById]);

  const closeMetricDetail = useCallback(() => {
    setDetailKey("");
    setCaseDetailId("");
  }, []);

  useEffect(() => {
    onMetricDetailChange?.(Boolean(metricDetailItem), closeMetricDetail, metricDetailItem);
  }, [metricDetailItem, closeMetricDetail, onMetricDetailChange]);

  useEffect(() => () => onMetricDetailChange?.(false, () => {}, null), [onMetricDetailChange]);

  const caseDetailTestCase = useMemo(() => {
    if (!caseDetailId) return null;
    const key = String(caseDetailId);
    return (
      executionCasesById.get(key) ||
      flyoutCasesById.get(caseDetailId) ||
      flyoutCasesById.get(key) ||
      null
    );
  }, [caseDetailId, executionCasesById, flyoutCasesById]);

  if (metricDetailItem) {
    return (
      <>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            bgcolor: "#FFFFFF",
          }}
        >
          <ExecutionScopeItemDetailView
            item={metricDetailItem}
            scopeMode="metric"
            caseTableMode="evidenceResults"
            testCasesById={flyoutCasesById}
            evidenceCasesById={executionCasesById}
            onBack={closeMetricDetail}
            onViewCaseDetails={setCaseDetailId}
            showHeader={false}
          />
        </Box>
        <SelectDropdownTheme>
          <AddTestCaseDialog
            open={Boolean(caseDetailTestCase)}
            onClose={() => setCaseDetailId("")}
            onSave={() => {}}
            metrics={metrics}
            risks={risks}
            editingTestCase={caseDetailTestCase}
            viewOnly
            projectId={projectId}
          />
        </SelectDropdownTheme>
      </>
    );
  }

  if (!results.length) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: { xs: 2, md: 3 },
          py: 2.5,
          bgcolor: "#FAFAFA",
        }}
      >
        <Box sx={{ ...SCORECARD_SURFACE_CARD_SX, py: 6, textAlign: "center" }}>
          <Typography
            component="h2"
            sx={{ fontFamily: "Lato, sans-serif", fontSize: 20, fontWeight: 700, color: "#0F172A" }}
          >
            Quality Scorecard
          </Typography>
          <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 14, color: "#64748B", mt: 1 }}>
            Run execution from Review &amp; Execute to populate metric results here.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        px: { xs: 2, md: 3 },
        py: 2.5,
        bgcolor: "#FAFAFA",
      }}
    >
      <Box sx={SCORECARD_SURFACE_CARD_SX}>
        <Box sx={{ mb: 2, minWidth: 0, width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "flex-start" },
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography
              component="h2"
              id="gate2-quality-scorecard-heading"
              sx={{
                fontFamily: "Lato, sans-serif",
                fontSize: 20,
                fontWeight: 700,
                lineHeight: "28px",
                color: "#0F172A",
                minWidth: 0,
              }}
            >
              Quality Scorecard
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexShrink: 0,
                alignSelf: { xs: "flex-end", sm: "flex-start" },
              }}
            >
            <Button
              variant="outlined"
              disableElevation
              disableRipple
              onClick={onAddCustomTestCase}
              sx={GHOST_PRIMARY_MEDIUM_BTN_SX}
            >
              Add Custom Test Case
            </Button>
            </Box>
          </Box>
          <Typography
            component="p"
            sx={{
              fontFamily: "Lato, sans-serif",
              fontSize: { xs: 13, sm: 14 },
              fontWeight: 400,
              lineHeight: "20px",
              color: "#64748B",
              mt: 1,
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflowWrap: { xs: "break-word", md: "normal" },
              wordBreak: { xs: "break-word", md: "normal" },
            }}
          >
            Review collected evidence and metric outcomes before proceeding to the Gate 2 release decision.
          </Typography>
        </Box>

        {qualityGateFailed && alertOpen ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              px: 2.5,
              py: 1.75,
              mb: 2,
              borderRadius: "8px",
              border: "1px solid #FECACA",
              bgcolor: "#FFF5F5",
            }}
          >
            <ErrorOutlineIcon sx={{ color: "#DC2626", fontSize: 22, mt: 0.15, flexShrink: 0 }} />
            <Typography
              sx={{
                flex: 1,
                fontFamily: "Lato, sans-serif",
                fontSize: 14,
                lineHeight: "22px",
                color: "#991B1B",
              }}
            >
              <Box component="span" sx={{ fontWeight: 700 }}>
                Quality Gate: Failed -
              </Box>{" "}
              <Box component="span" sx={{ fontWeight: 400 }}>
                {qualityGateBannerFailedCount} of {summary.total} metrics are below target threshold.
              </Box>
            </Typography>
            <IconButton size="small" onClick={() => setAlertOpen(false)} aria-label="Dismiss alert" sx={{ mt: -0.5 }}>
              <CloseIcon sx={{ fontSize: 18, color: "#94A3B8" }} />
            </IconButton>
          </Box>
        ) : null}

        <Box
          sx={{
            mb: 2,
            border: "1px solid #E6E6E6",
            borderRadius: "12px",
            bgcolor: "#FFFFFF",
            overflow: "hidden",
          }}
        >
        <Box
          sx={{
            p: 2.5,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <ScorecardInnerPanel title="Standard Metrics">
            <Typography
              component="div"
              sx={{
                fontFamily: "Lato, sans-serif",
                lineHeight: "100%",
                letterSpacing: 0,
              }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: 18,
                  fontWeight: 700,
                  fontStyle: "normal",
                  lineHeight: "100%",
                  color: "#00754E",
                }}
              >
                {summary.passedCount}
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: 18,
                  fontWeight: 700,
                  fontStyle: "normal",
                  lineHeight: "100%",
                  color: "#707070",
                }}
              >
                {" / "}
                {summary.total}
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: 18,
                  fontWeight: 700,
                  fontStyle: "normal",
                  lineHeight: "100%",
                  color: "#707070",
                }}
              >
                {" "}
                Passed
              </Box>
            </Typography>
          </ScorecardInnerPanel>

          <ScorecardInnerPanel title="Custom Test Cases">
            {customTestCaseCount > 0 ? (
              <>
                <Typography component="div" sx={SCORECARD_PANEL_VALUE_GREY_SX}>
                  {customTestCaseCount} in scope
                </Typography>
                {ephemeralCustomRunCount > 0 && customTestCaseRows.length === 0 ? (
                  <Typography
                    sx={{
                      fontFamily: "Lato, sans-serif",
                      fontSize: 12,
                      fontWeight: 400,
                      lineHeight: "100%",
                      color: "#707070",
                    }}
                  >
                    Run Once only — save with Save & Execute to list details below.
                  </Typography>
                ) : null}
              </>
            ) : (
              <Typography component="div" sx={SCORECARD_PANEL_VALUE_GREY_SX}>
                —
              </Typography>
            )}
          </ScorecardInnerPanel>
        </Box>
      </Box>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            minHeight: 40,
            mb: 0,
            borderBottom: "1px solid #E6E6E6",
            "& .MuiTab-root": {
              textTransform: "none",
              fontFamily: "Lato, sans-serif",
              fontSize: 14,
              fontWeight: 500,
              fontStyle: "normal",
              lineHeight: "100%",
              letterSpacing: 0,
              minHeight: 40,
              color: "#767676",
            },
            "& .MuiTab-root.Mui-selected": {
              color: "#005BF0",
              fontWeight: 700,
            },
            "& .MuiTabs-indicator": { bgcolor: "#005BF0", height: 2 },
          }}
        >
          <Tab
            value="standard"
            label="Standard Metrics"
            id="gate2-scorecard-tab-standard"
            aria-controls="gate2-scorecard-panel"
          />
          <Tab
            value="custom"
            label="Custom Test Cases"
            id="gate2-scorecard-tab-custom"
            aria-controls="gate2-scorecard-panel"
          />
        </Tabs>

      <Box
        component="section"
        role="tabpanel"
        id="gate2-scorecard-panel"
        aria-labelledby={tab === "custom" ? "gate2-scorecard-tab-custom" : "gate2-scorecard-tab-standard"}
        sx={{ mb: 2, pt: 0, mt: 2 }}
      >
      {tab === "standard" ? (
          <MetricsTable rows={summary.rows} onViewDetails={setDetailKey} />
      ) : customTestCaseRows.length ? (
          <CustomTestCasesTable rows={customTestCaseRows} onViewDetails={setDetailKey} />
      ) : (
        <EmptyState
          icon={CUSTOM_TEST_CASES_EMPTY_ICON}
          iconIncludesContainer
          title={
            ephemeralCustomRunCount > 0
              ? CUSTOM_TEST_CASES_EPHEMERAL_EMPTY_COPY.title
              : CUSTOM_TEST_CASES_EMPTY_COPY.title
          }
          description={
            ephemeralCustomRunCount > 0
              ? CUSTOM_TEST_CASES_EPHEMERAL_EMPTY_COPY.description
              : CUSTOM_TEST_CASES_EMPTY_COPY.description
          }
          headingLevel="h3"
        />
      )}
      </Box>

      <Box sx={{ mt: 3, border: "1px solid #E6E6E6", borderRadius: "12px", p: 2.5 }}>
        <Typography component="h3" sx={{ ...RISK_SUMMARY_TITLE_SX, mb: 2 }}>
          Risk Assessment Summary
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
          <Box>
            <Typography sx={{ ...RISK_SUMMARY_BLOCKING_HEADING_SX, mb: 1 }}>Blocking Issues</Typography>
            {blockingBulletItems.length ? (
              <RiskSummaryBulletList items={blockingBulletItems} BulletIcon={RedBulletPoint} />
            ) : (
              <Typography sx={RISK_SUMMARY_EMPTY_SX}>
                No blocking issues identified for this run.
              </Typography>
            )}
          </Box>
          <Box>
            <Typography sx={{ ...RISK_SUMMARY_MITIGATED_HEADING_SX, mb: 1 }}>Mitigated Issues</Typography>
            {mitigatedBulletItems.length ? (
              <RiskSummaryBulletList items={mitigatedBulletItems} BulletIcon={BrownBulletPoint} />
            ) : (
              <Typography sx={RISK_SUMMARY_EMPTY_SX}>No medium or low risks in the register.</Typography>
            )}
          </Box>
        </Box>
      </Box>
      </Box>{/* SCORECARD_SURFACE_CARD_SX */}

    </Box>
  );
}
